/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import DataTable, { Row, Column } from "components/DataTable";
import Modal from "components/Modal";
import GenericToaster from "components/Toaster";
import apiService from "services/apiService";
import AddRouterForm from "./AddRouter";
import EditRouterInfo from "./EditRouterInfo";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { BannerContext } from "components/SideBar";

interface RouterInfo {
  id: number | string;
  routerName: string;
  macAddress: string;
  publicIp?: string | null;
  updatedAt: string;
}

const formatDate = (isoString: string): string => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const RouterInfoTab: React.FC = () => {
  const [data, setData] = useState<RouterInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRouter, setSelectedRouter] = useState<Row | null>(null);
  const [toastData, setToastData] = useState<{
    message: string;
    severity: "info" | "error";
  } | null>(null);

  const { isBannerVisible } = useContext(BannerContext);
      const dynamicHeightClass = isBannerVisible
        ? "h-[calc(100vh-144px)] min-h-[300px]"
        : "h-[calc(100vh-115px)] min-h-[300px]";

  const fetchRouters = () => {
    setLoading(true);
    apiService
      .get("api/router-info/company/all")
      .then((res) => setData(res.data || []))
      .catch(() => {
        setToastData({
          message: "Failed to fetch router info",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRouters();
  }, []);

  const handleOpenEdit = (row: Row) => {
    setSelectedRouter(row);
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setSelectedRouter(null);
  };

  const handleSaveEdit = (values: { routerName: string; macAddress: string; publicIp: string }) => {
    const routerId = selectedRouter?.id;
    if (!routerId) {
      setToastData({
        message: "Router id not found",
        severity: "error",
      });
      return;
    }

    setUpdating(true);
    apiService
      .put(`api/router-info/company/update/${routerId}`, {
        routerName: values.routerName,
        macAddress: values.macAddress,
        publicIp: values.publicIp || null,
      })
      .then(() => {
        handleCloseEdit();
        fetchRouters();
        setToastData({
          message: "Router info updated successfully",
          severity: "info",
        });
      })
      .catch((err) => {
        const msg = err?.response?.data || "Failed to update router info";
        setToastData({
          message: msg,
          severity: "error",
        });
      })
      .finally(() => setUpdating(false));
  };

  const handleOpenDelete = (row: Row) => {
    setSelectedRouter(row);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleting) {
      return;
    }

    const routerId = selectedRouter?.id;
    if (!routerId) {
      setToastData({
        message: "Router id not found",
        severity: "error",
      });
      return;
    }

    setDeleting(true);
    apiService
      .delete(`api/router-info/company/delete/${routerId}`)
      .then(() => {
        setOpenDeleteModal(false);
        setSelectedRouter(null);
        fetchRouters();
        setToastData({
          message: "Router info deleted successfully",
          severity: "info",
        });
      })
      .catch((err) => {
        const msg = err?.response?.data || "Failed to delete router info";
        setToastData({
          message: msg,
          severity: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  const columns: Column[] = [
    {
      key: "routerName",
      label: "Router Name",
      bold: true,
    },
    {
      key: "macAddress",
      label: "MAC Address",
    },
    {
      key: "publicIp",
      label: "Public IP",
      render: (value: unknown) => (value ? String(value) : "-"),
    },
    {
      key: "updatedAt",
      label: "Date",
      render: (value: unknown) => formatDate(value as string),
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row) => (
        <div className="flex items-center gap-2">
          {/* Edit */}
          <span
            onClick={() => handleOpenEdit(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
          >
            <FaEdit size={16} />
          </span>

          {/* Delete */}
          <button
            onClick={() => handleOpenDelete(row)}
            className="btn-red px-2 py-2 rounded-lg"

          >
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      ),
    },
  ];

  const tableData: Row[] = data.map((item, index) => ({
    id: item.id ?? `${index}`,
    ...item,
  }));

  return (
    <div className="flex flex-col text-slate-800 dark:text-slate-200 ">
      {/* Metric */}
      {/* <div className="flex gap-2 mb-2 mt-1">
        <div className="flex items-center justify-between p-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm min-w-[180px]">
          <div>
            <p className="text-xs text-slate-500">Total Routers</p>
            <h3 className="text-xl font-bold">
              {loading ? "..." : data.length}
            </h3>
          </div>
          <div className="h-10 w-10 flex items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Wifi size={20} />
          </div>
        </div>
      </div> */}

      {/* Table */}
      <DataTable
        data={tableData}
        columns={columns}
        loading={loading}
        onAddBtnClick={() => setOpenModal(true)}
        heightClass={dynamicHeightClass}

      />

      {/* Modal - Add */}
      {openModal && (
        <Modal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          header="Add Router Info"
          custemHeight="auto"
          customWidth="800px" // Increased width to accommodate 3 inputs
        >
          <AddRouterForm
            saving={saving}
            onCancel={() => setOpenModal(false)}
            onSubmit={(entries) => {
              setSaving(true);
              // Clean up empty IPs before sending
              const payload = entries.map(e => ({
                ...e,
                publicIp: e.publicIp.trim() || null
              }));

              apiService
                .post("api/router-info/company/save", payload)
                .then(() => {
                  setOpenModal(false);
                  fetchRouters();
                  setToastData({
                    message: "Router info saved successfully",
                    severity: "info",
                  });
                })
                .catch((err) => {
                  const msg =
                    err?.response?.data || "Failed to save router info";
                  setToastData({
                    message: msg,
                    severity: "error",
                  });
                })
                .finally(() => setSaving(false));
            }}
          />
        </Modal>
      )}

      {/* Modal - Edit */}
      {openEditModal && (
        <Modal
          isOpen={openEditModal}
          onClose={handleCloseEdit}
          header="Update Router Info"
          custemHeight="auto"
          customWidth="620px"
        >
          <EditRouterInfo
            initialValues={{
              routerName: String(selectedRouter?.routerName ?? ""),
              macAddress: String(selectedRouter?.macAddress ?? ""),
              publicIp: String(selectedRouter?.publicIp ?? ""),
            }}
            saving={updating}
            onSubmit={handleSaveEdit}
            onCancel={handleCloseEdit}
          />
        </Modal>
      )}

      {/* Modal - Delete */}
      {openDeleteModal && (
        <Modal
          isOpen={openDeleteModal}
          onClose={() => {
            setOpenDeleteModal(false);
            setSelectedRouter(null);
          }}
          onAccept={handleConfirmDelete}
          onDecline={() => {
            setOpenDeleteModal(false);
            setSelectedRouter(null);
          }}
          acceptText={deleting ? "Deleting..." : "Delete"}
          cancelText="Cancel"
          header="Delete Router"
          customWidth="500px"
        >
          <p className="text-base text-slate-600 dark:text-slate-300">
            Do you want to delete this router?
          </p>
        </Modal>
      )}

      {/* Toast */}
      {toastData && (
        <GenericToaster
          open={true}
          message={toastData.message}
          severity={toastData.severity}
          onClose={() => setToastData(null)}
        />
      )}
    </div>
  );
};

export default RouterInfoTab;